import { useAsyncData } from '#imports'
import { type MaybeRefOrGetter, computed, toRef } from 'vue'

import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import algoliasearch from 'algoliasearch'

import { algoliaHitToVariant } from '../../helpers/recommendationHelper'

import type {
  AlgoliaRecommendationsModel,
  AlgoliaRecommendationsObjectId,
  AlgoliaRecommendationsOptions,
} from './useAlgoliaRecommendations.types'

export function useAlgoliaRecommendations(
  objectID: MaybeRefOrGetter<AlgoliaRecommendationsObjectId>,
  model: AlgoliaRecommendationsModel,
  options?: MaybeRefOrGetter<AlgoliaRecommendationsOptions | undefined>,
) {
  const objectIDRef = toRef(objectID)
  const optionsRef = toRef(options)

  const $httpFetch = useHttpFetch()

  const { data: configurationData } = useAsyncData(
    'algoliaRecommendationsConfig',
    () => $httpFetch('/bm/recommendation/configuration'),
    { dedupe: 'defer' },
  )

  const queryKey = computed(() =>
    JSON.stringify({
      indexName: configurationData.value?.indexName,
      objectID: objectIDRef.value,
      model,
      ...optionsRef.value,
    }),
  )

  return useAsyncData(
    queryKey.value,
    async () => {
      if (!objectIDRef.value || !configurationData.value?.indexName) return null

      const algoliaSearchOption = useAlgoliaSearchOptions('recommendations')
      const algoliaClient = algoliasearch(
        'dummy_app_id',
        'dummy_key',
        algoliaSearchOption,
      )

      return algoliaClient.getRecommendations<AlgoliaProduct>([
        {
          indexName: configurationData.value?.indexName,
          objectID: objectIDRef.value,
          model,
          ...optionsRef.value,
        },
      ])
    },
    {
      lazy: true,
      server: false,
      dedupe: 'defer',
      default: () => [],
      deep: false,
      watch: [queryKey],
      transform: (response) => {
        if (!response) {
          return []
        }

        const { results } = response

        return results[0]?.hits.map((hit) => algoliaHitToVariant(hit))
      },
    },
  )
}
